import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { setSnackbar } from "../../../../../../util/state/globalAction";
import { GlobalContext } from "../../../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../../../util/state/globalVariables";
import { execBookingAction, getVehiclePlates, useBookingByIdWithMetaQueryObserver } from "../../../../api/services";
import DialogCloseButton from "../../../../components/DialogCloseButton";
import { FormAutocomplete } from "../../../../components/fields";
import useDialog from "../../../../hooks/useDialog";
import { BookingAction, ReactQueryKey, RouteParams } from "../../../../types";

interface AssignVehicleFormValues {
    licensePlate: {
        value: string,
        label: string
    } | null
}

const AssignVehicleAction = () => {

    const { t } = useTranslation('booking');

    const { dispatchGlobalData } = useContext(GlobalContext);

    const { id } = useParams<RouteParams>();

    const dialog = useDialog();

    const bookingQuery = useBookingByIdWithMetaQueryObserver(id);
    const vehiclePlatesQuery = useQuery(
        [ReactQueryKey.VehiclePlates, bookingQuery.data?.booking.pickupLocation.id],
        () => {
            if (!bookingQuery.data) {
                return null;
            }
            return getVehiclePlates({
                locationCode: bookingQuery.data.booking.pickupLocation.id
            });
        },
        {
            enabled: false
        }
    );

    const execBookingActionMutation = useMutation(
        execBookingAction,
        {
            onError: () => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            },
            onSuccess: () => {
                bookingQuery.refetch();
            },
            onSettled: () => {
                dialog.handleClose()
            }
        }
    );

    const form = useForm<AssignVehicleFormValues>({
        defaultValues: {
            licensePlate: null
        }
    });

    const handleClick = useCallback(() => {
        vehiclePlatesQuery.refetch();
        form.reset();
        dialog.handleShow();
    }, []);
    const onSubmit = useCallback((data: AssignVehicleFormValues) => {
        if (!id || !bookingQuery.data) {
            return;
        }
        execBookingActionMutation.mutate({
            bookingId: id,
            action: BookingAction.AssignVehicle,
            lastUpdateTime: bookingQuery.data?.booking.requestUpdateDate,
            assignedPlateNumber: data.licensePlate?.value
        });
    }, [id, bookingQuery.data]);

    useEffect(() => {
        if (bookingQuery.data && bookingQuery.data.booking.assignedPlateNumber) {
            const label = (
                bookingQuery.data.booking.assignedPlateNumber +
                ` (${bookingQuery.data.booking.carGroup.acrissCode} - ${bookingQuery.data.booking.carGroup.cargroupModel})`
            )
            form.reset({
                licensePlate: {
                    value: bookingQuery.data.booking.assignedPlateNumber,
                    label: label
                }
            });
        }
    }, [bookingQuery.data]);

    return (
        <>
            <Button onClick={handleClick}>{t("label.assignVehicle")}</Button>
            <Dialog open={dialog.open} maxWidth="sm" fullWidth>
                <DialogCloseButton onClick={dialog.handleClose} />
                <DialogTitle>{t("label.assignVehicle")}</DialogTitle>
                <DialogContent>
                    <FormAutocomplete<AssignVehicleFormValues>
                        name="licensePlate"
                        label={t("label.selectVehicle")}
                        control={form.control}
                        options={vehiclePlatesQuery.data?.vehicles ?? []}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        loading={vehiclePlatesQuery.isLoading}
                    />
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={dialog.handleClose}>
                        {t("label.back")}
                    </Button>
                    <Button onClick={form.handleSubmit(onSubmit)} disabled={!form.formState.isDirty}>
                        {t("label.save")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AssignVehicleAction;
